import type { wave } from '@round/api';
import { formatMoney } from '@/utility/money';

const DAY_MS = 24 * 60 * 60 * 1000;
const HOUR_MS = 60 * 60 * 1000;

export const PRICE_TYPE_LABEL: Record<wave.ContractPriceType, string> = {
    per_post: 'Fixed fee',
    full_set: 'Bundle',
    cpm: 'CPM',
};

// Contract statuses in StatusPill's vocabulary, so the pills share one palette.
export const CONTRACT_PILL_STATUS: Record<wave.ContractStatus, string> = {
    proposed: 'invited',
    accepted: 'active',
    declined: 'declined',
    fulfilled: 'completed',
    expired: 'closed',
    cancelled: 'closed',
    resolved_pro_rata: 'completed',
};

export const CONTRACT_STATUS_LABEL: Record<wave.ContractStatus, string> = {
    proposed: 'Awaiting your reply',
    accepted: 'Accepted',
    declined: 'Declined',
    fulfilled: 'Complete',
    expired: 'Ended',
    cancelled: 'Withdrawn',
    resolved_pro_rata: 'Paid for posts made',
};

export type ContractSubmitBlock = 'not_accepted' | 'window_closed' | 'all_posted' | 'full' | 'ended' | 'post_limit';

const formatPosts = (count: number) => `${count} ${count === 1 ? 'post' : 'posts'}`;

export const isBundle = (contract: Pick<wave.Contract, 'price_type'>): boolean => contract.price_type === 'full_set';

const isReachableOffer = (contract: Pick<wave.Contract, 'status'>): boolean =>
    contract.status !== 'declined' && contract.status !== 'cancelled';

/** The posts the contract is for: what the creator accepted, else what was offered. Null is uncapped. */
export const getContractPostCap = (contract: wave.Contract): number | null =>
    contract.accepted_max_posts ?? contract.offered_max_posts;

/**
 * How many posts a card says the contract is for. An uncapped contract takes the invite's
 * limit, which the backend counts across the invite, so it's 'shared' (and not shown) once
 * several uncapped contracts split it.
 */
export type PostCap = number | 'unlimited' | 'shared';

const toPostCap = (cap: number | null): PostCap => cap ?? 'unlimited';

const formatDisplayPrice = (contract: wave.CreatorContract): string =>
    formatMoney(contract.display_currency.symbol, contract.display_price ?? contract.price);

/** "£150.00 for 3 posts" / "£40.00 per post · up to 3 posts" / "£1.50 CPM · unlimited posts". */
export const formatContractTerms = (
    contract: wave.CreatorContract,
    cap: PostCap = toPostCap(contract.effective_max_posts)
): string => {
    const money = formatDisplayPrice(contract);
    if (isBundle(contract)) {
        return `${money} for ${formatPosts(getContractPostCap(contract) ?? contract.min_posts)}`;
    }
    const unit = contract.price_type === 'cpm' ? 'CPM' : 'per post';
    if (cap === 'shared') {
        return `${money} ${unit}`;
    }
    return `${money} ${unit} · ${cap === 'unlimited' ? 'unlimited posts' : `up to ${formatPosts(cap)}`}`;
};

// A bundle pays pro rata if its window closes short, which creators aren't told for now.
const PAYOUT_UNLOCKS: Record<wave.ContractPriceType, string> = {
    full_set: 'When every post is approved',
    per_post: 'After post approval',
    cpm: 'As approved views grow',
};

export const getPayoutUnlocks = (contract: Pick<wave.Contract, 'price_type'>): string =>
    PAYOUT_UNLOCKS[contract.price_type];

/** When an invite's offers pay out, or null when they differ and each offer card says its own. */
export const getSharedPayoutUnlocks = (contracts: Pick<wave.Contract, 'status' | 'price_type'>[]): string | null => {
    const reachable = contracts.filter(isReachableOffer);
    const offers = reachable.length > 0 ? reachable : contracts;
    const rules = new Set(offers.map(getPayoutUnlocks));
    if (rules.size !== 1) {
        return null;
    }
    return [...rules][0];
};

export type OfferSummary = {
    label: string;
    amount: string;
    detail: string;
    postingLimit: string;
    payoutUnlocks: string;
    isWithdrawn: boolean;
    /** The amount is a ceiling (a CPM offer's max earnable) rather than a price. */
    isMaximum: boolean;
};

/**
 * What an invite's overview says about its one contract, from the contract's own terms: the
 * invite's offer fields can read a bundle's total as a per-post price, and a CPM invite's
 * max earnable ignores the contract's own post cap.
 */
export const getOfferSummary = (contracts: wave.CreatorContract[]): OfferSummary | null => {
    // A declined or withdrawn offer beside a live one doesn't make it several; with none
    // live, a lone closed offer still describes the invite.
    const reachable = contracts.filter(isReachableOffer);
    const offers = reachable.length > 0 ? reachable : contracts;
    if (offers.length !== 1) {
        return null;
    }
    const [contract] = offers;
    const money = formatDisplayPrice(contract);
    if (contract.status === 'cancelled') {
        return {
            label: `${PRICE_TYPE_LABEL[contract.price_type]} offer`,
            amount: money,
            detail: 'Withdrawn by the brand',
            postingLimit: 'No more posts',
            payoutUnlocks: getPayoutUnlocks(contract),
            isWithdrawn: true,
            isMaximum: false,
        };
    }
    if (isBundle(contract)) {
        const posts = formatPosts(getContractPostCap(contract) ?? contract.min_posts);
        return {
            label: 'Bundle offer',
            amount: money,
            detail: `for ${posts}, paid as one amount`,
            postingLimit: posts,
            payoutUnlocks: getPayoutUnlocks(contract),
            isWithdrawn: false,
            isMaximum: false,
        };
    }
    const cap = contract.effective_max_posts;
    if (contract.price_type === 'cpm') {
        const symbol = contract.display_currency.symbol;
        return {
            label: 'Maximum earnable',
            amount: contract.max_earnable === null ? 'Unlimited' : formatMoney(symbol, contract.max_earnable),
            detail: `${money} CPM · ${
                contract.max_payout === null
                    ? 'no per-post maximum'
                    : `${formatMoney(symbol, contract.max_payout)} maximum per post`
            }`,
            postingLimit: cap === null ? 'Unlimited' : formatPosts(cap),
            payoutUnlocks: getPayoutUnlocks(contract),
            isWithdrawn: false,
            isMaximum: contract.max_earnable !== null,
        };
    }
    return {
        label: 'Per-post offer',
        amount: money,
        detail: cap === null ? 'per post · unlimited posts' : `per post · up to ${formatPosts(cap)}`,
        postingLimit: cap === null ? 'Unlimited' : formatPosts(cap),
        payoutUnlocks: getPayoutUnlocks(contract),
        isWithdrawn: false,
        isMaximum: false,
    };
};

/** What a bundle's single earning paid for: "Bundle · 3 of 3 posts", or settled short. */
export const formatBundleEarning = (contract: wave.Contract): string => {
    const total = getContractPostCap(contract) ?? contract.min_posts;
    const { approved } = contract.post_counts;
    const base = `Bundle · ${approved} of ${formatPosts(total)}`;
    return contract.status === 'resolved_pro_rata' || approved < total ? `${base}, paid pro rata` : base;
};

/** Each contract's post cap as its card shows it. */
export const getCardPostCaps = (contracts: wave.CreatorContract[]): Map<number, PostCap> => {
    const uncapped = contracts.filter(
        (contract) =>
            getContractPostCap(contract) === null && contract.status !== 'declined' && contract.status !== 'cancelled'
    );
    return new Map(
        contracts.map((contract) => {
            if (getContractPostCap(contract) === null && uncapped.length > 1) {
                return [contract.id, 'shared'];
            }
            return [contract.id, toPostCap(contract.effective_max_posts)];
        })
    );
};

export const formatWindowLength = (durationHours: number | null): string | null => {
    if (durationHours === null) {
        return null;
    }
    if (durationHours % 24 === 0) {
        const days = durationHours / 24;
        return `${days} ${days === 1 ? 'day' : 'days'}`;
    }
    return `${durationHours} hours`;
};

/** How long is left to post, or that the window has closed. Null when the contract has no window. */
export const formatWindowLeft = (
    contract: Pick<wave.Contract, 'expires_at'>,
    nowMs: number,
    action = 'post'
): string | null => {
    if (contract.expires_at === null) {
        return null;
    }
    const diffMs = new Date(contract.expires_at).getTime() - nowMs;
    if (diffMs <= 0) {
        return 'Posting window closed';
    }
    if (diffMs >= DAY_MS) {
        const days = Math.ceil(diffMs / DAY_MS);
        return `${days} ${days === 1 ? 'day' : 'days'} left to ${action}`;
    }
    const hours = Math.max(1, Math.ceil(diffMs / HOUR_MS));
    return `${hours} ${hours === 1 ? 'hour' : 'hours'} left to ${action}`;
};

const hasLivePost = (contract: Pick<wave.Contract, 'post_counts'>): boolean =>
    contract.post_counts.approved + contract.post_counts.pending > 0;

/**
 * The posting-window line for an accepted contract. A bundle's window bounds every post;
 * a fixed fee's or CPM's only the first, after which the creator can keep posting while
 * the campaign is open. Whether the window has actually shut is the backend's call
 * (reservation_expired), not the clock's.
 */
export const formatContractWindow = (
    contract: wave.CreatorContract,
    nowMs: number,
    cap: PostCap = toPostCap(contract.effective_max_posts)
): string | null => {
    if (contract.status !== 'accepted' || contract.expires_at === null) {
        return null;
    }
    if (contract.submission_availability === 'reservation_expired') {
        return 'Posting window closed';
    }
    // A bundle's window still matters while a post is in review, since a rejection reopens a slot.
    if (getContractPostsLeft(contract, cap) === 0 && (!isBundle(contract) || contract.post_counts.pending === 0)) {
        return null;
    }
    if (isBundle(contract)) {
        return formatWindowLeft(contract, nowMs);
    }
    if (hasLivePost(contract)) {
        return 'Keep posting until the campaign ends';
    }
    return formatWindowLeft(contract, nowMs, 'make your first post');
};

/** Posts still to make: approved and in-review posts use a slot, rejected ones free it. Null is uncapped. */
export const getContractPostsLeft = (
    contract: wave.CreatorContract,
    cap: PostCap = toPostCap(contract.effective_max_posts)
): number | null => {
    if (typeof cap !== 'number') {
        return null;
    }
    return Math.max(0, cap - contract.post_counts.approved - contract.post_counts.pending);
};

export const formatContractProgress = (
    contract: wave.CreatorContract,
    cap: PostCap = toPostCap(contract.effective_max_posts)
): string => {
    const { approved, pending } = contract.post_counts;
    const approvedLabel = typeof cap === 'number' ? `${approved} of ${cap} approved` : `${approved} approved`;
    return pending > 0 ? `${approvedLabel} · ${pending} in review` : approvedLabel;
};

/** Why a post can't go against this contract yet, or null when it can. */
export const getContractSubmitBlock = (
    contract: wave.CreatorContract,
    cap: PostCap = toPostCap(contract.effective_max_posts)
): ContractSubmitBlock | null => {
    if (contract.status !== 'accepted') {
        return 'not_accepted';
    }
    const availability = contract.submission_availability;
    if (availability === 'reservation_expired') {
        return 'window_closed';
    }
    if (getContractPostsLeft(contract, cap) === 0) {
        return 'all_posted';
    }
    if (availability === 'full' || availability === 'ended') {
        return availability;
    }
    if (availability === 'already_submitted') {
        return 'post_limit';
    }
    return null;
};

/** A proposed contract can be accepted while the campaign can still fund it. */
export const canAcceptContract = (contract: wave.CreatorContract): boolean =>
    contract.status === 'proposed' && contract.submission_availability === 'open';

/** A lapsed contract with no live post can be taken up again on the same terms. */
/**
 * A contract with no live post the creator can take up again on the same terms, as the
 * backend allows: a fixed fee or CPM whose window lapsed while still accepted, or a bundle
 * once settlement has expired it (an accepted bundle still holds its price until then).
 */
export const canReacceptContract = (contract: wave.CreatorContract): boolean => {
    if (hasLivePost(contract)) {
        return false;
    }
    if (isBundle(contract)) {
        return contract.status === 'expired';
    }
    return contract.status === 'accepted' && contract.submission_availability === 'reservation_expired';
};

const isOfferContract = (contract: Pick<wave.Contract, 'price_type' | 'duration_hours'>): boolean =>
    contract.price_type !== 'full_set' && contract.duration_hours === null;

/**
 * The declined offer the invite can still take back up: its own per-post or CPM offer,
 * newest first, while no other copy of that offer is live. Null when there is none.
 */
export const getReconsiderableContractId = (
    contracts: Pick<wave.Contract, 'id' | 'status' | 'price_type' | 'duration_hours' | 'created'>[]
): number | null => {
    const offers = contracts.filter(isOfferContract);
    if (offers.some((contract) => contract.status === 'proposed' || contract.status === 'accepted')) {
        return null;
    }
    const declined = offers
        .filter((contract) => contract.status === 'declined')
        .sort((a, b) => b.created.localeCompare(a.created) || b.id - a.id);
    return declined[0]?.id ?? null;
};

/** Offers the creator can still act on or has worked under: a declined or withdrawn one can't be reached again. */
export const countOffers = (contracts: Pick<wave.Contract, 'status'>[]): number =>
    contracts.filter(isReachableOffer).length;

const STATUS_ORDER: Record<wave.ContractStatus, number> = {
    proposed: 0,
    accepted: 1,
    fulfilled: 2,
    resolved_pro_rata: 2,
    expired: 3,
    declined: 4,
    cancelled: 5,
};

/** Proposals first, then the ones in play, then the finished ones; newest first within each. */
export const sortContracts = <T extends wave.Contract>(contracts: T[]): T[] =>
    [...contracts].sort((a, b) => STATUS_ORDER[a.status] - STATUS_ORDER[b.status] || b.id - a.id);
